import {
  CheckCircleIcon as CheckCircleIconOutline,
  HandThumbDownIcon,
  HandThumbUpIcon,
  StarIcon as StarIconOutline,
  XCircleIcon as XCircleIconOutline,
} from "@heroicons/react/24/outline";
import { Badge, Menu } from "@mantine/core";
import { useState, useEffect } from "react";
import { useSubmitDocumentFeedback } from "../../../../hooks/QueryHooks";
import type { DocumentFeedbackType } from "../../../../services/signalDocuments/signalDocumentsEndpoints.interface";
import notify from "../../../../utils/notify";

type Props = {
  documentId: string;
  workspaceId: string;
  currentFeedback?: DocumentFeedbackType;
};

const FEEDBACK_CONFIG: Record<
  DocumentFeedbackType,
  { label: string; color: string }
> = {
  star: { label: "Starred", color: "yellow.6" },
  resolve: { label: "Resolved", color: "green.6" },
  dismiss: { label: "Dismissed", color: "red.6" },
  thumbs_up: { label: "Helpful", color: "blue.6" },
  thumbs_down: { label: "Not Helpful", color: "orange.6" },
};

const DocumentFeedbackControls = ({ documentId, workspaceId, currentFeedback }: Props) => {
  const [localFeedback, setLocalFeedback] = useState<DocumentFeedbackType | undefined>(currentFeedback);
  const feedbackMutation = useSubmitDocumentFeedback();

  useEffect(() => {
    setLocalFeedback(currentFeedback);
  }, [currentFeedback]);

  const onSelect = (feedbackType: DocumentFeedbackType) => {
    const previous = localFeedback;
    setLocalFeedback(feedbackType);

    feedbackMutation.mutate(
      {
        workspace_id: workspaceId,
        document_id: documentId,
        feedback_type: feedbackType,
      },
      {
        onSuccess: () => {
          notify({
            messageList: [`Feedback set to ${FEEDBACK_CONFIG[feedbackType].label}`],
            type: "info",
          });
        },
        onError: () => {
          setLocalFeedback(previous);
          notify({
            messageList: ["Failed to submit feedback"],
            type: "failure",
          });
        },
      },
    );
  };

  const config = localFeedback ? FEEDBACK_CONFIG[localFeedback] : null;

  return (
    <Menu shadow="md" width={180} position="bottom-end">
      <Menu.Target>
        <Badge
          radius="sm"
          color={config?.color || "gray"}
          variant={config ? "dot" : "outline"}
          className="hover:opacity-90 !cursor-pointer transition-opacity !lowercase !capitalize"
        >
          {config?.label || "Set Feedback"}
        </Badge>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>Feedback</Menu.Label>
        <Menu.Item
          className={localFeedback === "star" ? "bg-gray-800 text-white" : ""}
          onClick={() => onSelect("star")}
          leftSection={<StarIconOutline width={20} />}
        >
          Star
        </Menu.Item>
        <Menu.Item
          className={localFeedback === "resolve" ? "bg-gray-800 text-white" : ""}
          onClick={() => onSelect("resolve")}
          leftSection={<CheckCircleIconOutline width={20} />}
        >
          Resolve
        </Menu.Item>
        <Menu.Item
          className={localFeedback === "dismiss" ? "bg-gray-800 text-white" : ""}
          onClick={() => onSelect("dismiss")}
          leftSection={<XCircleIconOutline width={20} />}
        >
          Dismiss
        </Menu.Item>
        <Menu.Item
          className={localFeedback === "thumbs_up" ? "bg-gray-800 text-white" : ""}
          onClick={() => onSelect("thumbs_up")}
          leftSection={<HandThumbUpIcon width={20} />}
        >
          Helpful
        </Menu.Item>
        <Menu.Item
          className={localFeedback === "thumbs_down" ? "bg-gray-800 text-white" : ""}
          onClick={() => onSelect("thumbs_down")}
          leftSection={<HandThumbDownIcon width={20} />}
        >
          Not Helpful
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};

export default DocumentFeedbackControls;
